import React, { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Calendar,
  Loader2,
  Printer,
  Table2,
  ChevronDown,
  ChevronRight,
  BarChart2,
  X,
  TrendingUp,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import api, { baseURL } from "~/services/api";
import { getFilenameFromContentDisposition, openBlobInNewTab } from "~/utils/blobPreview";
import { AlertContext } from "~/App";
import DREModal from "./dreModal";

const periodTypes = [
  { value: "specific_month", label: "Specific Month" },
  { value: "annual_monthly", label: "Annual Month by Month" },
  { value: "quarter", label: "Specific Quarter" },
  { value: "annual_accumulated", label: "Annual Accumulated" },
  { value: "mom", label: "MoM (Month vs Month)" },
  { value: "yoy", label: "YoY (Year vs Year)" },
];

const dateTypeOptions = [
  { value: "accrual_date", label: "Accrual Date" },
  { value: "settlement_date", label: "Settlement Date" },
  { value: "due_date", label: "Due Date" },
];

const months = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const monthNameToNumber = {
  "Jan": 1, "Feb": 2, "Mar": 3, "Apr": 4, "May": 5, "Jun": 6,
  "Jul": 7, "Aug": 8, "Sep": 9, "Oct": 10, "Nov": 11, "Dec": 12,
  "January": 1, "February": 2, "March": 3, "April": 4, "June": 6,
  "July": 7, "August": 8, "September": 9, "October": 10, "November": 11, "December": 12,
};

const expandableRows = [
  "OPERATING INCOME (BEFORE TAX)",
  "NET INCOME (AFTER ESTIMATED TAX)",
  "FEE",
  "DISCOUNTS",
];

const NON_INTERACTIVE_ROWS = new Set([
  "OPERATING INCOME (BEFORE TAX)",
  "TAX (estimated @ 27%)",
  "OPERATING MARGIN (%) (BEFORE TAX)",
  "NET INCOME (AFTER ESTIMATED TAX)",
  "NET INCOME (AFTER ESTIMATED TAX) %",
  "NET REVENUE",
]);

function DRERow({
  label,
  values,
  level = 0,
  onOpenChartIcon,
  onOpenDRECell,
  showChartIcon,
  expandedRows,
  setExpandedRows,
  showNR,
  showTE,
  showVariation,
  isRangePairs,
  pairCount,
  parentLabel = null,
  expandFirstLevel = false,
  expandAllChildren = false,
}) {
  const [expanded, setExpanded] = useState(level === 0);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (expandAllChildren) {
      setExpanded(true);
    } else if (expandFirstLevel) {
      setExpanded(level === 0);
    } else {
      setExpanded(false);
    }
  }, [expandAllChildren, expandFirstLevel]);
  const hasChildren = !!(values?.children && values.children.length > 0);
  const isExpandableRow = expandableRows.includes(label);
  const isRowExpanded = expandedRows.includes(label);

  const hasNR = values.percentage_nr != null;
  const hasTE = values.percentage_te != null;
  const bgColor = hasNR ? "bg-blue-50" : hasTE ? "bg-orange-50" : "bg-gray-50";

  const handleToggleExpandable = () => {
    if (isExpandableRow) {
      setExpandedRows(prev =>
        prev.includes(label)
          ? prev.filter(r => r !== label)
          : [...prev, label]
      );
    }
  };

  const childrenToShow = isExpandableRow && isRowExpanded
    ? values.children
    : (hasChildren && expanded ? values.children : []);

  const rowIsNonInteractive = NON_INTERACTIVE_ROWS.has(label);

  return (
    <>
      <tr
        className={`${level === 0 ? "bg-gray-100" : "bg-white"}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ backgroundColor: hovered ? "#ffedd5" : undefined }}
      >
        <td
          className="px-2 py-2 text-xs font-semibold sticky left-0 z-10 bg-inherit"
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          <div className="flex items-center gap-1">
            {isExpandableRow ? (
              isRowExpanded ? (
                <ChevronDown
                  size={14}
                  className="cursor-pointer text-mila_orange"
                  onClick={handleToggleExpandable}
                />
              ) : (
                <ChevronRight
                  size={14}
                  className="cursor-pointer text-mila_orange"
                  onClick={handleToggleExpandable}
                />
              )
            ) : hasChildren ? (
              expanded ? (
                <ChevronDown
                  size={14}
                  className="cursor-pointer"
                  onClick={() => setExpanded(!expanded)}
                />
              ) : (
                <ChevronRight
                  size={14}
                  className="cursor-pointer"
                  onClick={() => setExpanded(!expanded)}
                />
              )
            ) : null}
            {level === 0 && showChartIcon && !isExpandableRow ? (
              <BarChart2
                size={14}
                className="text-mila_orange cursor-pointer"
                onClick={() => onOpenChartIcon(label, values.columns)}
              />
            ) : null}
            <span>{label}</span>
          </div>
        </td>
        {isRangePairs
          ? (
            Array.from({ length: pairCount || 0 }).map((_, pIdx) => {
              const idxA = pIdx * 2;
              const idxB = pIdx * 2 + 1;
              const cols = values.columns || [];
              const isPercentageRow = label === "NET INCOME (AFTER ESTIMATED TAX) %" || label === "OPERATING MARGIN (%) (BEFORE TAX)";
              const renderCell = (val, i) => {
                const cellInteractive = typeof val === "number" && !isPercentageRow && !rowIsNonInteractive;
                return (
                  <>
                    <td
                      className={`px-2 py-2 text-xs text-right border-l ${cellInteractive ? "cursor-pointer hover:bg-blue-100" : "cursor-default"}`}
                      onClick={() => cellInteractive && onOpenDRECell(label, values.columns, i, parentLabel)}
                    >
                      {val == null
                        ? "N/A"
                        : (typeof val === "number"
                          ? `${isPercentageRow ? `${Number(val).toFixed(2)}%` : Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : "")}
                    </td>
                    {showNR && (
                      <td className="px-2 py-2 text-xs text-right border-l bg-blue-50">
                        {hasNR && values.percentage_nr
                          ? (values.percentage_nr[i] != null
                            ? `${values.percentage_nr[i].toFixed(2)}%`
                            : "N/A")
                          : ""}
                      </td>
                    )}
                    {showTE && (
                      <td className="px-2 py-2 text-xs text-right border-l bg-orange-50">
                        {hasTE && values.percentage_te
                          ? (values.percentage_te[i] != null
                            ? `${values.percentage_te[i].toFixed(2)}%`
                            : "N/A")
                          : ""}
                      </td>
                    )}
                  </>
                );
              };
              return (
                <React.Fragment key={`row-pair-${pIdx}`}>
                  {renderCell(cols[idxA], idxA)}
                  {renderCell(cols[idxB], idxB)}
                  {showVariation && (
                    <>
                      <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                        {Array.isArray(values.variation_amount)
                          ? (values.variation_amount[pIdx] != null
                            ? Number(values.variation_amount[pIdx]).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : "N/A")
                          : ""}
                      </td>
                      <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                        {Array.isArray(values.variation_percentage)
                          ? (values.variation_percentage[pIdx] != null
                            ? `${Number(values.variation_percentage[pIdx]).toFixed(2)}%`
                            : "N/A")
                          : ""}
                      </td>
                    </>
                  )}
                </React.Fragment>
              );
            })
          )
          : (
            <>
              {(values.columns || []).map((col, i) => {
                const isPercentageRow = label === "NET INCOME (AFTER ESTIMATED TAX) %" || label === "OPERATING MARGIN (%) (BEFORE TAX)";
                const cellInteractive = typeof col === "number" && !isPercentageRow && !rowIsNonInteractive;
                return (
                  <React.Fragment key={i}>
                    <td
                      className={`px-2 py-2 text-xs text-right border-l ${cellInteractive ? "cursor-pointer hover:bg-blue-100" : "cursor-default"}`}
                      onClick={() => cellInteractive && onOpenDRECell(label, values.columns, i, parentLabel)}
                    >
                      {col == null
                        ? "N/A"
                        : (typeof col === "number"
                          ? `${isPercentageRow ? `${Number(col).toFixed(2)}%` : Number(col).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : "")}
                    </td>
                    {showNR && (
                      <td className="px-2 py-2 text-xs text-right border-l bg-blue-50">
                        {hasNR && values.percentage_nr
                          ? (values.percentage_nr[i] != null
                            ? `${values.percentage_nr[i].toFixed(2)}%`
                            : "N/A")
                          : ""}
                      </td>
                    )}
                    {showTE && (
                      <td className="px-2 py-2 text-xs text-right border-l bg-orange-50">
                        {hasTE && values.percentage_te
                          ? (values.percentage_te[i] != null
                            ? `${values.percentage_te[i].toFixed(2)}%`
                            : "N/A")
                          : ""}
                      </td>
                    )}
                  </React.Fragment>
                );
              })}
              {showVariation && (
                <>
                  <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                    {Array.isArray(values.variation_amount)
                      ? (values.variation_amount[0] != null
                        ? Number(values.variation_amount[0]).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : "N/A")
                      : ""}
                  </td>
                  <td className="px-2 py-2 text-xs text-right border-l bg-yellow-50">
                    {Array.isArray(values.variation_percentage)
                      ? (values.variation_percentage[0] != null
                        ? `${Number(values.variation_percentage[0]).toFixed(2)}%`
                        : "N/A")
                      : ""}
                  </td>
                </>
              )}
            </>
          )}
      </tr>

      {childrenToShow.length > 0
        ? childrenToShow.map((child, i) => (
          <DRERow
            key={i}
            label={child.label}
            values={child}
            level={level + 1}
            onOpenChartIcon={onOpenChartIcon}
            onOpenDRECell={onOpenDRECell}
            showChartIcon={showChartIcon}
            expandedRows={expandedRows}
            setExpandedRows={setExpandedRows}
            showNR={showNR}
            showTE={showTE}
            showVariation={showVariation}
            isRangePairs={isRangePairs}
            pairCount={pairCount}
            parentLabel={label}
            expandFirstLevel={false}
            expandAllChildren={expandAllChildren}
          />
        ))
        : null}
    </>
  );
}

function KPIRow({ kpi, columns, onOpenChart, showChartIcon, showNR, showTE }) {
  const [hovered, setHovered] = useState(false);
  return (
    <tr
      className="bg-white"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ backgroundColor: hovered ? "#ffedd5" : undefined }}
    >
      <td className="px-2 py-2 text-xs font-semibold sticky left-0 z-10 bg-white border border-gray-300 border-t-0 min-w-[275px]">
        <div className="flex items-center gap-1">
          {showChartIcon && (
            <BarChart2
              size={14}
              className="text-mila_orange cursor-pointer"
              onClick={() => onOpenChart(kpi.label, kpi.values)}
            />
          )}
          <span>{kpi.label}</span>
        </div>
      </td>
      {(kpi.values || []).map((val, i) => (
        <React.Fragment key={i}>
          <td className="px-2 py-2 text-xs text-right border border-gray-300 border-l-0 border-t-0 w-28">
            {val == null ? "N/A" : (typeof val === "number" ? `${val.toFixed(2)}%` : "")}
          </td>
          {showNR && <td className="px-2 py-2 text-xs text-right border border-gray-300 border-l-0 border-t-0 w-12"></td>}
          {showTE && <td className="px-2 py-2 text-xs text-right border border-gray-300 border-l-0 border-t-0 w-12"></td>}
        </React.Fragment>
      ))}
    </tr>
  );
}

export default function FinancialIncomeStatement() {
  const { alertBox } = useContext(AlertContext);
  const filial = useSelector((state) => state.auth?.filial);
  const currentYear = new Date().getFullYear();
  const baseYearOptions = [currentYear - 2, currentYear - 1, currentYear];
  const compareYearOptions = [currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
  const [dreConfigs, setDreConfigs] = useState([]);
  const [selectedDreConfig, setSelectedDreConfig] = useState("");
  const [periodType, setPeriodType] = useState("annual_monthly");
  const [selectedMonth, setSelectedMonth] = useState(8);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(1);
  const [compareMonth, setCompareMonth] = useState(6);
  const [compareYear, setCompareYear] = useState(currentYear - 1);
  const [dreModalOpen, setDreModalOpen] = useState(false);
  const [dreModalData, setDreModalData] = useState(null);
  const [chartModal, setChartModal] = useState(null);
  const [columns, setColumns] = useState([]);
  const [dreData, setDreData] = useState([]);
  const [kpisData, setKpisData] = useState([]);
  const [title, setTitle] = useState("Income Statement");
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState([]);
  const [expandFirstLevel, setExpandFirstLevel] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [showNR, setShowNR] = useState(true);
  const [showTE, setShowTE] = useState(true);
  const [variationEnabled, setVariationEnabled] = useState(true);
  const [momRangeMode, setMomRangeMode] = useState(false);
  const [baseEndMonth, setBaseEndMonth] = useState(2);
  const [yoyRangeMode, setYoyRangeMode] = useState(false);
  const [variationMeta, setVariationMeta] = useState(null);
  const [dateType, setDateType] = useState("due_date");
  const [dateTypeTouched, setDateTypeTouched] = useState(false);
  const [fetchDebug, setFetchDebug] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    if (expandAll) {
      setExpandedRows([...expandableRows]);
    } else if (!expandAll) {
      setExpandedRows([]);
    }
  }, [expandAll]);

  async function fetchDreConfigs() {
    try {
      const { data } = await api.get("/dreconfigs-getall");
      setDreConfigs(data || []);
      if (data && data.length > 0) {
        setSelectedDreConfig(data[0].id);
        setDateType(data[0]?.date_type || "due_date");
        setDateTypeTouched(false);
      }
    } catch (e) {
      setDreConfigs([]);
    }
  }

  function mapAccountToRow(acc) {
    return {
      label: acc.code ? `${acc.code} ${acc.name}` : acc.name || acc.label,
      columns: acc.values || [],
      percentage_nr: acc.percentage_nr || null,
      percentage_te: acc.percentage_te || null,
      variation_amount: acc.variation_amount || null,
      variation_percentage: acc.variation_percentage || null,
      children: (acc.children || []).map(mapAccountToRow),
    };
  }

  function mapSectionsToRows(sections) {
    return (sections || []).map((s) => {
      const isOperatingIncome = s.label === "OPERATING INCOME (BEFORE TAX)";
      const isNetIncomeAfterTax = s.label === "NET INCOME (AFTER ESTIMATED TAX)";

      if (isOperatingIncome) {
        const marginSection = sections.find(
          sec => sec.label === "OPERATING MARGIN (%) (BEFORE TAX)"
        );
        const children = (s.accounts || []).map(mapAccountToRow);
        if (marginSection) {
          children.push({
            label: "OPERATING MARGIN (%) (BEFORE TAX)",
            columns: marginSection.values || [],
            percentage_nr: null,
            percentage_te: null,
            variation_amount: null,
            variation_percentage: null,
            children: [],
          });
        }
        return {
          label: s.label,
          columns: s.values || [],
          percentage_nr: s.percentage_nr || null,
          percentage_te: s.percentage_te || null,
          variation_amount: s.variation_amount || null,
          variation_percentage: s.variation_percentage || null,
          children,
        };
      }

      if (isNetIncomeAfterTax) {
        const percentageSection = sections.find(
          sec => sec.label === "NET INCOME (AFTER ESTIMATED TAX) %"
        );
        const children = (s.accounts || []).map(mapAccountToRow);
        if (percentageSection) {
          children.push({
            label: "NET INCOME (AFTER ESTIMATED TAX) %",
            columns: percentageSection.values || [],
            percentage_nr: null,
            percentage_te: null,
            variation_amount: null,
            variation_percentage: null,
            children: [],
          });
        }
        return {
          label: s.label,
          columns: s.values || [],
          percentage_nr: s.percentage_nr || null,
          percentage_te: s.percentage_te || null,
          variation_amount: s.variation_amount || null,
          variation_percentage: s.variation_percentage || null,
          children,
        };
      }

      if (
        s.label === "OPERATING MARGIN (%) (BEFORE TAX)" ||
        s.label === "NET INCOME (AFTER ESTIMATED TAX) %"
      ) {
        return null;
      }

      return {
        label: s.label,
        columns: s.values || [],
        percentage_nr: s.percentage_nr || null,
        percentage_te: s.percentage_te || null,
        variation_amount: s.variation_amount || null,
        variation_percentage: s.variation_percentage || null,
        children: (s.accounts || []).map(mapAccountToRow),
      };
    }).filter(Boolean);
  }

  async function fetchData() {
    if (!selectedDreConfig) return;

    setLoading(true);
    try {
      let url = "";
      let params = {};
      if (
        periodType === "specific_month" ||
        periodType === "annual_monthly" ||
        periodType === "quarter" ||
        periodType === "annual_accumulated"
      ) {
        url = "/dreconfigs/period";
        params = { dre_config_id: selectedDreConfig, period_type: periodType };
        if (periodType === "specific_month") {
          params.year = selectedYear;
          params.month = selectedMonth + 1;
        }
        if (periodType === "annual_monthly") {
          params.year = selectedYear;
        }
        if (periodType === "quarter") {
          params.year = selectedYear;
          params.quarter = selectedQuarter;
        }
        if (periodType === "annual_accumulated") {
          params.year = selectedYear;
        }
      } else if (periodType === "mom") {
        url = "/dreconfigs/compare-mom";
        params = {
          dre_config_id: selectedDreConfig,
          base_year: selectedYear,
          base_month: selectedMonth + 1,
        };

        if (momRangeMode) {
          params.base_end_month = baseEndMonth + 1;
          if (compareYear !== selectedYear - 1) {
            params.compare_year = compareYear;
          }
        } else {
          params.compare_year = compareYear;
          params.compare_month = compareMonth + 1;
        }
      } else if (periodType === "yoy") {
        if (yoyRangeMode) {
          url = "/dreconfigs/compare-yoy-range";
          params = {
            dre_config_id: selectedDreConfig,
            base_year: selectedYear,
            base_month: selectedMonth + 1,
            base_end_month: baseEndMonth + 1,
          };
          if (compareYear !== selectedYear - 1) {
            params.compare_year = compareYear;
          }
        } else {
          url = "/dreconfigs/compare-yoy";
          params = {
            dre_config_id: selectedDreConfig,
            base_year: selectedYear,
            compare_year: compareYear,
          };
        }
      }

      {
        const cfg = (dreConfigs || []).find((c) => String(c.id) === String(selectedDreConfig));
        const effectiveDateType = dateTypeTouched ? dateType : (cfg?.date_type || dateType || "due_date");
        if (effectiveDateType) {
          params.date_type = effectiveDateType;
        }
        setFetchDebug({
          url,
          params: { ...params },
          effectiveDateType,
          configDateType: cfg?.date_type || null,
          dropdownDateType: dateType,
          selectedDreConfig,
          timestamp: new Date().toISOString(),
        });
      
        try { console.debug("DRE Report fetch:", { url, params }); } catch {}
      }
      const { data } = await api.get(url, { params });
      const periodHeaders =
        (Array.isArray(data.periods) && data.periods.length > 0)
          ? data.periods
          : (Array.isArray(data.headers) && data.headers.length > 0)
            ? data.headers
            : (Array.isArray(data.period_headers) && data.period_headers.length > 0)
              ? data.period_headers
              : (Array.isArray(data.columns) && data.columns.length > 0)
                ? data.columns
                : [];
      setColumns(periodHeaders);
      setTitle(data.period_label || "Income Statement");
      setDreData(mapSectionsToRows(data.sections || []));
      setKpisData(data.kpis?.items || []);
      setVariationMeta(data.variation_meta || null);
    } catch (e) {
      setColumns([]);
      setTitle("Income Statement");
      setDreData([]);
      setKpisData([]);
      setVariationMeta(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
   
    fetchDreConfigs();
  }, [filial?.id]);

  useEffect(() => {
    if (momRangeMode && periodType === "mom") {
      setCompareYear(selectedYear - 1);
    }
  }, [momRangeMode, periodType, selectedYear]);

  useEffect(() => {
    if (yoyRangeMode && periodType === "yoy") {
      setCompareYear(selectedYear - 1);
    }
  }, [yoyRangeMode, periodType, selectedYear]);

  useEffect(() => {
    if (selectedDreConfig) {
      fetchData();
    }
  }, [
    selectedDreConfig,
    periodType,
    selectedMonth,
    selectedYear,
    selectedQuarter,
    compareMonth,
    compareYear,
    momRangeMode,
    baseEndMonth,
    yoyRangeMode,
    dateType,
    filial?.id,
  ]);


  useEffect(() => {
    if (!selectedDreConfig || !Array.isArray(dreConfigs) || dreConfigs.length === 0) return;
    const cfg = dreConfigs.find((c) => String(c.id) === String(selectedDreConfig));
    if (!cfg) return;
    const cfgDateType = cfg.date_type || "due_date";
    setDateType(cfgDateType);
    setDateTypeTouched(false);
  }, [selectedDreConfig, dreConfigs]);

  const showChartIcon =
    periodType === "annual_monthly" ||
    periodType === "quarter" ||
    periodType === "mom" ||
    periodType === "yoy";
  const variationAllowed = periodType === "mom" || periodType === "yoy";
  const showVariation = variationAllowed && variationEnabled;
  const variationHeaders = showVariation
    ? (variationMeta?.headers
      || variationMeta?.columns
      || variationMeta?.titles
      || variationMeta?.labels
      || [])
    : [];
  const pairCountMeta = Number(variationMeta?.pair_count) || 0;
  const computedPairCount = pairCountMeta > 0
    ? pairCountMeta
    : (Array.isArray(columns) ? Math.floor(columns.length / 2) : 0);
  const isRangePairs = (
    (variationMeta?.placement === "after_each_pair" && computedPairCount > 1)
    || (((periodType === "mom" && momRangeMode) || (periodType === "yoy" && yoyRangeMode)) && computedPairCount > 1)
  );
  const nrCount = showNR ? 1 : 0;
  const teCount = showTE ? 1 : 0;

  const handleOpenChartModal = (label, columnsValues) => {
    if ((columns || []).length <= 1) return;
    const pairs = (columns || []).map((c, i) => ({
      name: String(c),
      value1: Number(columnsValues?.[i] || 0),
    }));
    
    const data = pairs.filter(p => !/total/i.test(p.name));
    if (data.length === 0) return;
    setChartModal({ label, data });
  };

  const handleOpenDREModal = (label, columnsValues, columnIndex, parentLabel = null) => {
    const rawPeriod = columns[columnIndex] || "-";
    const value = columnsValues?.[columnIndex] || 0;
    let normalizedPeriod = rawPeriod;
    const isLastColumn = columnIndex === columns.length - 1;
    if ((periodType === "annual_monthly" || periodType === "annual_accumulated") && isLastColumn) {

      normalizedPeriod = `year${selectedYear}`;
    }

    if (periodType === "yoy") {
      
      if (yoyRangeMode) {
        const rangeMatch = String(rawPeriod).match(/^(\d{4})-([A-Za-z]+)-([A-Za-z]+)$/);
        if (rangeMatch) {
          const year = rangeMatch[1];
          const startMonthName = rangeMatch[2];
          const endMonthName = rangeMatch[3];
          const startMonthNum = monthNameToNumber[startMonthName];
          const endMonthNum = monthNameToNumber[endMonthName];
          if (startMonthNum && endMonthNum) {
            normalizedPeriod = `yoyrange=${year}-${startMonthNum}-${endMonthNum}`;
          }
        }
      } else {
        const yearMatch = String(rawPeriod).match(/^\d{4}$/);
        if (yearMatch) {
          normalizedPeriod = `year${yearMatch[0]}`;
        }
      }
    }
    if (periodType === "quarter" && isLastColumn) {
      normalizedPeriod = `totalquarter=Q${selectedQuarter}-${selectedYear}`;
    }

    const specialLines = {
      "GROSS REVENUES": "gross_revenue",
      "DISCOUNTS": "discounts_revenue",
      "DISCOUNTS FROM GROSS REVENUE (-)": "deduction",
      "TOTAL EXPENSES": "expense"
    };

    let isSpecialLine = specialLines[label];

    const codeMatch = (label || "").match(/^[\d.]+/);
    const accountCode = codeMatch ? codeMatch[0] : null;
    if (!isSpecialLine) {
      if (accountCode === "01.001.007" || accountCode === "01.001.007.001") {
        isSpecialLine = "revenue_fee";
      }
    }
    if (!isSpecialLine && label === "DISCOUNTS" && parentLabel === "DISCOUNTS FROM GROSS REVENUE (-)") {
      isSpecialLine = "discounts_revenue";
    }

    const cfg = (dreConfigs || []).find((c) => String(c.id) === String(selectedDreConfig));
    const effectiveDateType = dateTypeTouched ? dateType : (cfg?.date_type || dateType || "due_date");

    const modalData = {
      accountLabel: label,
      period: normalizedPeriod,
      value: value,
      type: isSpecialLine || null,
      dreConfigId: selectedDreConfig,
      dateType: effectiveDateType
    };

    setDreModalData(modalData);
    setDreModalOpen(true);
  };

  const buildPeriodExportParams = () => {
    if (!selectedDreConfig) {
      toast("Select an IS Configuration first", { type: "error", autoClose: 2500 });
      return null;
    }

    if (periodType === "mom" || periodType === "yoy") {
      toast("Export/Print is available only for period reports", { type: "error", autoClose: 3000 });
      return null;
    }

    const params = {
      dre_config_id: selectedDreConfig,
      period_type: periodType,
    };

    if (periodType === "specific_month") {
      params.year = selectedYear;
      params.month = selectedMonth + 1;
    }
    if (periodType === "annual_monthly" || periodType === "annual_accumulated") {
      params.year = selectedYear;
    }
    if (periodType === "quarter") {
      params.year = selectedYear;
      params.quarter = selectedQuarter;
    }

    const cfg = (dreConfigs || []).find((c) => String(c.id) === String(selectedDreConfig));
    const effectiveDateType = dateTypeTouched ? dateType : (cfg?.date_type || dateType || "due_date");
    if (effectiveDateType) {
      params.date_type = effectiveDateType;
    }

    return params;
  };

  const handleExportExcel = async (firstLevelOnly = false) => {
    const params = buildPeriodExportParams();
    if (!params) return;

    setExportingExcel(true);
    try {
      const exportParams = { ...params, first_level_only: firstLevelOnly };
      const response = await api.get("/dreconfigs/period/excel", {
        params: exportParams,
        responseType: "blob",
      });

      const contentType = String(response.headers["content-type"] || "");
      if (contentType.includes("application/json")) {
        const raw = await response.data.text();
        const parsed = JSON.parse(raw || "{}");
        const name = parsed?.name;
        if (!name) throw new Error("Invalid export response");
        saveAs(`${baseURL}/get-file/${name}`, name.endsWith(".xlsx") ? name : `${name}.xlsx`);
      } else {
        const filename =
          getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
          `dre_period_${Date.now()}.xlsx`;
        const fileBlob = new Blob([response.data], {
          type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(fileBlob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`);
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to export Excel", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePrint = async (firstLevelOnly = false) => {
    const params = buildPeriodExportParams();
    if (!params) return;

    setGeneratingPdf(true);
    try {
      const exportParams = { ...params, first_level_only: firstLevelOnly };
      const response = await api.get("/dreconfigs/period/pdf", {
        params: exportParams,
        responseType: "blob",
      });

      const filename =
        getFilenameFromContentDisposition(response.headers["content-disposition"]) ||
        `dre_period_${Date.now()}.pdf`;
      const pdfBlob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const opened = openBlobInNewTab(pdfBlob, {
        filename: filename.endsWith(".pdf") ? filename : `${filename}.pdf`,
      });
      if (!opened) {
        toast("Pop-up blocked. Allow pop-ups to preview the PDF.", {
          type: "error",
          autoClose: 3000,
        });
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate PDF", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGeneratingPdf(false);
    }
  };

  const askFirstLevelOnlyAndRun = (onConfirm) => {
    alertBox({
      title: "Warning",
      description: "Do you want to export only the first levels?",
      buttons: [
        {
          title: "No",
          onPress: async () => {
            await onConfirm(false);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            await onConfirm(true);
          },
        },
      ],
    });
  };

  return (
    <div className="w-full h-full flex flex-col bg-white border overflow-hidden rounded-r-xl">
      <div className="bg-gray-100 px-4 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-gray-700" />
          <h2 className="text-lg font-bold text-gray-700">IS Report</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => askFirstLevelOnlyAndRun(handleExportExcel)}
            className="min-w-[130px] px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            disabled={exportingExcel || generatingPdf}
          >
            {exportingExcel ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Table2 size={16} />
                Excel
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => askFirstLevelOnlyAndRun(handlePrint)}
            className="min-w-[130px] px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            disabled={generatingPdf || exportingExcel}
          >
            {generatingPdf ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Printer size={16} />
                Print
              </>
            )}
          </button>
        </div>
      </div>

      <div className="px-4 py-3 border-b bg-gray-50 flex flex-wrap gap-3 items-end justify-between">
        <div className="flex flex-wrap gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">IS Configuration</label>
            <select
              value={selectedDreConfig}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedDreConfig(value);
               
                const cfg = (dreConfigs || []).find((c) => String(c.id) === String(value));
                setDateType(cfg?.date_type || "due_date");
                setDateTypeTouched(false);
              }}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            >
              {dreConfigs.length === 0 ? (
                <option value="">No configurations available</option>
              ) : (
                dreConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.dre_name} ({config.dre_code})
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Date Type</label>
            <select
              value={dateType}
              onChange={(e) => { setDateType(e.target.value); setDateTypeTouched(true); }}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            >
              {dateTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Period Type</label>
            <select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value)}
              className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
            >
              {periodTypes.map((pt) => (
                <option key={pt.value} value={pt.value}>
                  {pt.label}
                </option>
              ))}
            </select>
          </div>

          {periodType === "specific_month" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Month</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {months.map((m, idx) => (
                    <option key={idx} value={idx}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {baseYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {periodType === "quarter" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Quarter</label>
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  <option value={1}>Q1</option>
                  <option value={2}>Q2</option>
                  <option value={3}>Q3</option>
                  <option value={4}>Q4</option>
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {baseYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {(periodType === "annual_monthly" || periodType === "annual_accumulated") && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-600">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
              >
                {baseYearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}

          {periodType === "mom" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">
                  {momRangeMode ? "Start Month" : "Base Month"}
                </label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {months.map((m, idx) => (
                    <option key={idx} value={idx}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {momRangeMode && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-600">End Month</label>
                  <select
                    value={baseEndMonth}
                    onChange={(e) => setBaseEndMonth(Number(e.target.value))}
                    className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx} disabled={idx < selectedMonth}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Base Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {baseYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {!momRangeMode && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-semibold text-gray-600">Compare Month</label>
                  <select
                    value={compareMonth}
                    onChange={(e) => setCompareMonth(Number(e.target.value))}
                    className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                  >
                    {months.map((m, idx) => (
                      <option key={idx} value={idx}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">
                  {momRangeMode ? "Compare Year (Optional)" : "Compare Year"}
                </label>
                <select
                  value={compareYear}
                  onChange={(e) => setCompareYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {compareYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">&nbsp;</label>
                <label className="flex items-center gap-2 cursor-pointer h-[30px]">
                  <input
                    type="checkbox"
                    checked={momRangeMode}
                    onChange={(e) => setMomRangeMode(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                  />
                  <span className="text-xs font-medium text-gray-700">Range Mode</span>
                </label>
              </div>
            </>
          )}

          {periodType === "yoy" && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">Base Year</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {baseYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              {yoyRangeMode && (
                <>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600">Start Month</label>
                    <select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(Number(e.target.value))}
                      className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                    >
                      {months.map((m, idx) => (
                        <option key={idx} value={idx}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-semibold text-gray-600">End Month</label>
                    <select
                      value={baseEndMonth}
                      onChange={(e) => setBaseEndMonth(Number(e.target.value))}
                      className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                    >
                      {months.map((m, idx) => (
                        <option key={idx} value={idx} disabled={idx < selectedMonth}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">{yoyRangeMode ? "Compare Year (Optional)" : "Compare Year"}</label>
                <select
                  value={compareYear}
                  onChange={(e) => setCompareYear(Number(e.target.value))}
                  className="px-2 py-1.5 text-xs border rounded-sm focus:ring-1 focus:ring-mila_orange"
                >
                  {compareYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-600">&nbsp;</label>
                <label className="flex items-center gap-2 cursor-pointer h-[30px]">
                  <input
                    type="checkbox"
                    checked={yoyRangeMode}
                    onChange={(e) => setYoyRangeMode(e.target.checked)}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                  />
                  <span className="text-xs font-medium text-gray-700">Range Mode</span>
                </label>
              </div>
            </>
          )}
        </div>

        <div className="flex items-end gap-8">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Expand Levels</label>
            <div className="flex items-center gap-3 h-[30px]">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expandFirstLevel}
                  onChange={(e) => {
                    setExpandFirstLevel(e.target.checked);
                    if (e.target.checked) setExpandAll(false);
                  }}
                  className="w-4 h-4 text-gray-600 border-gray-300 rounded-sm focus:ring-gray-500"
                />
                <span className="text-xs font-medium text-gray-700">First Level</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={expandAll}
                  onChange={(e) => {
                    setExpandAll(e.target.checked);
                    if (e.target.checked) setExpandFirstLevel(false);
                  }}
                  className="w-4 h-4 text-gray-600 border-gray-300 rounded-sm focus:ring-gray-500"
                />
                <span className="text-xs font-medium text-gray-700">Expand All</span>
              </label>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-600">Show Columns</label>
            <div className="flex items-center gap-3 h-[30px]">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showNR}
                  onChange={(e) => setShowNR(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500"
                />
                <span className="text-xs font-medium text-gray-700">NR</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTE}
                  onChange={(e) => setShowTE(e.target.checked)}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded-sm focus:ring-orange-500"
                />
                <span className="text-xs font-medium text-gray-700">TE</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={variationEnabled}
                  onChange={(e) => setVariationEnabled(e.target.checked)}
                  disabled={!variationAllowed}
                  className="w-4 h-4 text-yellow-600 border-gray-300 rounded-sm focus:ring-yellow-500 disabled:opacity-50"
                />
                <span className="text-xs font-medium text-gray-700">Variation</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {fetchDebug?.show && (
        <div className="px-4 py-2 bg-yellow-50 border-b text-[11px] text-gray-700 tracking-tight">
          <div>
            <span className="font-semibold">API:</span> <span className="font-mono">{fetchDebug.url}</span>
            <span className="mx-2 text-gray-400">|</span>
            <span className="font-semibold">date_type sent:</span> <span className="font-mono">{String(fetchDebug.effectiveDateType)}</span>
            <span className="mx-2 text-gray-400">|</span>
            <span className="font-semibold">config date_type:</span> <span className="font-mono">{String(fetchDebug.configDateType)}</span>
            <span className="mx-2 text-gray-400">|</span>
            <span className="font-semibold">dropdown date_type:</span> <span className="font-mono">{String(fetchDebug.dropdownDateType)}</span>
            <span className="mx-2 text-gray-400">|</span>
            <span className="font-semibold">dre_config_id:</span> <span className="font-mono">{String(fetchDebug.selectedDreConfig)}</span>
          </div>
          <div className="mt-1">
            <span className="font-semibold">params:</span> <span className="font-mono break-all">{JSON.stringify(fetchDebug.params)}</span>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto p-4">
        <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
          <Calendar size={14} />
          <span>Period: {title}</span>
        </div>

        <div className="overflow-x-auto mb-6">
          <table className="w-full border-collapse min-w-max">
            <thead className="sticky top-0 bg-gray-200 z-10">
              <tr>
                <th className="px-2 py-2 text-xs font-semibold text-left border sticky left-0 bg-gray-200 z-20">
                  Description
                </th>
                {isRangePairs
                  ? (
                    Array.from({ length: computedPairCount }).map((_, pIdx) => (
                      <React.Fragment key={`pair-${pIdx}`}>
                        <th className="px-2 py-2 text-xs font-semibold text-right border w-28">
                          {columns[pIdx * 2]}
                        </th>
                        {showNR && (
                          <th className="px-2 py-2 text-xs font-semibold text-right border bg-blue-100 w-12">NR</th>
                        )}
                        {showTE && (
                          <th className="px-2 py-2 text-xs font-semibold text-right border bg-orange-100 w-12">TE</th>
                        )}

                        <th className="px-2 py-2 text-xs font-semibold text-right border w-28">
                          {columns[pIdx * 2 + 1]}
                        </th>
                        {showNR && (
                          <th className="px-2 py-2 text-xs font-semibold text-right border bg-blue-100 w-12">NR</th>
                        )}
                        {showTE && (
                          <th className="px-2 py-2 text-xs font-semibold text-right border bg-orange-100 w-12">TE</th>
                        )}

                        {variationHeaders.map((header, hIdx) => (
                          <th key={`var-${pIdx}-${hIdx}`} className="px-2 py-2 text-xs font-semibold text-right border bg-yellow-100 w-28">
                            {header}
                          </th>
                        ))}
                      </React.Fragment>
                    ))
                  )
                  : (
                    <>
                      {columns.map((col, idx) => (
                        <React.Fragment key={idx}>
                          <th className="px-2 py-2 text-xs font-semibold text-right border w-28">{col}</th>
                          {showNR && <th className="px-2 py-2 text-xs font-semibold text-right border bg-blue-100 w-12">NR</th>}
                          {showTE && <th className="px-2 py-2 text-xs font-semibold text-right border bg-orange-100 w-12">TE</th>}
                        </React.Fragment>
                      ))}
                      {showVariation && variationHeaders.map((header, idx) => (
                        <th key={`var-${idx}`} className="px-2 py-2 text-xs font-semibold text-right border bg-yellow-100 w-28">{header}</th>
                      ))}
                    </>
                  )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="px-2 py-4 text-xs text-gray-500" colSpan={
                    isRangePairs
                      ? 1 + computedPairCount * (2 * (1 + nrCount + teCount) + variationHeaders.length)
                      : 1 + columns.length * (1 + nrCount + teCount) + variationHeaders.length
                  }>
                    Loading...
                  </td>
                </tr>
              ) : dreData.length === 0 ? (
                <tr>
                  <td className="px-2 py-4 text-xs text-gray-500" colSpan={
                    isRangePairs
                      ? 1 + computedPairCount * (2 * (1 + nrCount + teCount) + variationHeaders.length)
                      : 1 + columns.length * (1 + nrCount + teCount) + variationHeaders.length
                  }>
                    No data
                  </td>
                </tr>
              ) : (
                dreData.map((item, idx) => (
                  <DRERow
                    key={idx}
                    label={item.label}
                    values={item}
                    onOpenChartIcon={handleOpenChartModal}
                    onOpenDRECell={handleOpenDREModal}
                    showChartIcon={showChartIcon}
                    expandedRows={expandedRows}
                    setExpandedRows={setExpandedRows}
                    showNR={showNR}
                    showTE={showTE}
                    showVariation={showVariation}
                    isRangePairs={isRangePairs}
                    pairCount={computedPairCount}
                    expandFirstLevel={expandFirstLevel}
                    expandAllChildren={expandAll}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {kpisData.length > 0 && (
          <div className="mt-8">
            <div className="overflow-x-auto">
              <table className="w-full min-w-max" style={{ borderSpacing: 0 }}>
                <thead className="bg-green-100">
                  <tr>
                    <th className="px-2 py-2 text-xs font-semibold text-left border border-gray-300 sticky left-0 bg-green-100 z-20 min-w-[275px]">
                      KPI
                    </th>
                    {columns.map((col, idx) => (
                      <React.Fragment key={idx}>
                        <th className="px-2 py-2 text-xs font-semibold text-right border border-gray-300 border-l-0 w-28">
                          {col}
                        </th>
                        {showNR && <th className="px-2 py-2 text-xs font-semibold text-right border border-gray-300 border-l-0 w-12"></th>}
                        {showTE && <th className="px-2 py-2 text-xs font-semibold text-right border border-gray-300 border-l-0 w-12"></th>}
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {kpisData.map((kpi, idx) => (
                    <KPIRow
                      key={idx}
                      kpi={kpi}
                      columns={columns}
                      onOpenChart={handleOpenChartModal}
                      showChartIcon={showChartIcon}
                      showNR={showNR}
                      showTE={showTE}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <DREModal
        isOpen={dreModalOpen}
        onClose={() => setDreModalOpen(false)}
        modalData={dreModalData}
      />

      {chartModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[750px] h-[400px] relative p-4 flex flex-col">
            <button
              className="absolute right-3 top-3 text-gray-500 hover:text-black"
              onClick={() => setChartModal(null)}
            >
              <X size={18} />
            </button>
            <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
              <BarChart2 className="text-mila_orange" size={16} />
              {chartModal.label}
            </h3>

            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartModal.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(v) => {
                  const n = Number(v || 0);
                  if (!isFinite(n)) return '';
                  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }} />
                <Tooltip formatter={(value) => {
                  const n = Number(value || 0);
                  if (!isFinite(n)) return value;
                  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }} />
                <Legend />
                <Bar dataKey="value1" fill="#EE5827" name="Period" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
